import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import type { MemberAdminDTO } from "../../../shared/dto";
import { HEARING_QUESTIONS } from "../../../shared/hearing-questions";
import { ScrollSyncTable } from "../../components/ScrollSyncTable";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  ErrorBanner,
  SuccessBanner,
  Banner,
} from "../../components/ui";

const MEMBER_STATUS_LABELS: Record<string, string> = {
  active: "有効",
  payment_failed: "決済失敗",
  suspended: "停止",
  cancelled: "解約",
  pending_activation: "仮登録",
};
const MEMBER_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  active: "success",
  payment_failed: "danger",
  suspended: "warning",
  cancelled: "neutral",
  pending_activation: "warning",
};

const STAGE_Q = HEARING_QUESTIONS.find((q) => q.code === "business_stage")!;
const REVENUE_Q = HEARING_QUESTIONS.find((q) => q.code === "annual_revenue_band")!;
const EMPLOYEE_Q = HEARING_QUESTIONS.find((q) => q.code === "employee_band")!;
function optLabel(q: { options?: { value: string; label: string }[] }, v: string | null): string {
  if (!v) return "-";
  return q.options?.find((o) => o.value === v)?.label ?? v;
}

interface CreateForm {
  email: string;
  password: string;
  displayName: string;
  memberNo: string; // 空なら自動生成
}
const INITIAL_CREATE: CreateForm = {
  email: "",
  password: "",
  displayName: "",
  memberNo: "",
};

const PER_PAGE = 100;

export function AdminMembersPage() {
  const [items, setItems] = useState<(MemberAdminDTO & { companyName?: string; businessStage?: string; revenueBand?: string; employeeBand?: string; isStaff?: number })[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [businessStage, setBusinessStage] = useState("");
  const [revenueBand, setRevenueBand] = useState("");
  const [employeeBand, setEmployeeBand] = useState("");
  const [total, setTotal] = useState<number | null>(null);
  const [memberTotal, setMemberTotal] = useState<number | null>(null);
  const [filtered, setFiltered] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    id: string;
    status: string;
    memberLabel: string;
  } | null>(null);
  // 新規作成
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(INITIAL_CREATE);
  const [createBusy, setCreateBusy] = useState(false);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (businessStage) params.set("businessStage", businessStage);
      if (revenueBand) params.set("revenueBand", revenueBand);
      if (employeeBand) params.set("employeeBand", employeeBand);
      params.set("page", String(targetPage));
      params.set("perPage", String(PER_PAGE));
      const r = await api.get<{
        items: typeof items;
        total: number;
        memberTotal: number;
      }>(`/admin/members?${params.toString()}`);
      setItems(r.items);
      setTotal(r.total);
      setMemberTotal(r.memberTotal);
      setPage(targetPage);
      setFiltered(
        Boolean(q || status || businessStage || revenueBand || employeeBand),
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestChange = (id: string, st: string) => {
    setMsg(null);
    setErr(null);
    const row = items.find((m) => m.id === id);
    setPending({
      id,
      status: st,
      memberLabel: row ? `${row.memberNo} ${row.displayName}` : id,
    });
  };
  const cancelChange = () => setPending(null);
  const confirmChange = async () => {
    if (!pending) return;
    setErr(null);
    const label = MEMBER_STATUS_LABELS[pending.status] ?? pending.status;
    try {
      await api.patch(`/admin/members/${pending.id}/status`, {
        status: pending.status,
      });
      setMsg(`${pending.memberLabel} のステータスを「${label}」に変更しました`);
      setPending(null);
      await load();
    } catch (e) {
      setErr((e as Error).message ?? "変更に失敗しました");
    }
  };

  const submitCreate = async () => {
    setErr(null);
    setMsg(null);
    if (
      !createForm.email ||
      !createForm.password ||
      !createForm.displayName
    ) {
      setErr("メール・パスワード・表示名は必須です");
      return;
    }
    if (createForm.password.length < 8) {
      setErr("パスワードは 8 文字以上で入力してください");
      return;
    }
    if (createForm.memberNo && !/^[A-Z0-9]{1,32}$/.test(createForm.memberNo)) {
      setErr("会員番号は英大文字と数字のみ (1-32 文字) です");
      return;
    }
    setCreateBusy(true);
    try {
      const r = await api.post<{
        ok: boolean;
        id: string;
        memberNo: string;
      }>("/admin/members", {
        email: createForm.email,
        password: createForm.password,
        displayName: createForm.displayName,
        memberNo: createForm.memberNo || undefined,
      });
      setMsg(
        `会員を作成しました (会員番号 ${r.memberNo} / ${createForm.displayName})`,
      );
      setCreateForm(INITIAL_CREATE);
      setOpenCreate(false);
      await load();
    } catch (e) {
      setErr((e as Error).message ?? "作成に失敗しました");
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div>
      <SectionHeading
        level={2}
        action={
          <button
            className="btn-primary text-sm"
            onClick={() => {
              setErr(null);
              setMsg(null);
              setOpenCreate((v) => !v);
            }}
          >
            {openCreate ? "閉じる" : "+ 新規会員を作成"}
          </button>
        }
      >
        会員管理
      </SectionHeading>

      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <span className="text-sm text-tora-ink/60">
          {filtered ? "絞り込み結果" : "会員数"}
        </span>
        <span className="text-2xl font-bold text-tora">
          {memberTotal ?? "—"}
        </span>
        <span className="text-sm text-tora-ink/60">名</span>
        {total != null && memberTotal != null && total > memberTotal && (
          <span className="text-xs text-tora-ink/50">
            （スタッフ {total - memberTotal} 名を除く／一覧は全 {total} 名を表示）
          </span>
        )}
      </div>

      {openCreate && (
        <div className="card p-5 mb-4 space-y-3 border-l-4 border-l-tora">
          <h3 className="font-bold text-base">新規会員を作成</h3>
          <p className="text-xs text-tora-ink/60">
            会員サイトにログイン可能なアカウントを作成します。初回ログイン時に
            ヒアリング入力が求められます。
          </p>
          <div>
            <label className="label">メールアドレス *</label>
            <input
              className="input"
              type="email"
              value={createForm.email}
              onChange={(e) =>
                setCreateForm({ ...createForm, email: e.target.value })
              }
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="例: member@example.com"
            />
          </div>
          <div>
            <label className="label">初期パスワード * (8 文字以上)</label>
            <input
              className="input"
              type="text"
              value={createForm.password}
              onChange={(e) =>
                setCreateForm({ ...createForm, password: e.target.value })
              }
              minLength={8}
              placeholder="会員に通知するパスワード"
            />
            <p className="text-[11px] text-tora-ink/50 mt-1">
              会員には別途口頭 or メールで通知してください。会員は初回ログイン後、
              マイページから変更を推奨します。
            </p>
          </div>
          <div>
            <label className="label">表示名 *</label>
            <input
              className="input"
              value={createForm.displayName}
              onChange={(e) =>
                setCreateForm({ ...createForm, displayName: e.target.value })
              }
              placeholder="例: 山田 太郎"
            />
          </div>
          <div>
            <label className="label">会員番号 (任意)</label>
            <input
              className="input"
              value={createForm.memberNo}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  memberNo: e.target.value.toUpperCase(),
                })
              }
              placeholder="空欄で自動生成 (M + 10 字)"
            />
            <p className="text-[11px] text-tora-ink/50 mt-1">
              英大文字・数字のみ。1〜32 文字。空欄なら自動生成されます。
            </p>
          </div>
          {err && openCreate && <ErrorBanner>{err}</ErrorBanner>}
          <div className="flex items-center gap-2">
            <button
              className="btn-primary"
              onClick={submitCreate}
              disabled={createBusy}
            >
              {createBusy ? "作成中…" : "作成"}
            </button>
            <button
              className="btn-outline text-sm"
              onClick={() => {
                setCreateForm(INITIAL_CREATE);
                setOpenCreate(false);
                setErr(null);
              }}
              disabled={createBusy}
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <input
          className="input max-w-xs"
          placeholder="氏名 / 会員番号 / メール"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="input max-w-[200px]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">全ステータス</option>
          <option value="active">有効</option>
          <option value="payment_failed">決済失敗</option>
          <option value="suspended">停止</option>
          <option value="cancelled">解約</option>
          <option value="pending_activation">仮登録</option>
        </select>
        <select className="input max-w-[140px]" value={businessStage} onChange={(e) => setBusinessStage(e.target.value)}>
          <option value="">事業ステージ</option>
          {STAGE_Q.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="input max-w-[160px]" value={revenueBand} onChange={(e) => setRevenueBand(e.target.value)}>
          <option value="">年商帯</option>
          {REVENUE_Q.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="input max-w-[130px]" value={employeeBand} onChange={(e) => setEmployeeBand(e.target.value)}>
          <option value="">従業員数</option>
          {EMPLOYEE_Q.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button className="btn-primary" onClick={() => load(1)}>
          検索
        </button>
      </div>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      {msg && <SuccessBanner>{msg}</SuccessBanner>}
      {pending && (
        <Banner tone="warning">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">
              <strong>{pending.memberLabel}</strong> のステータスを「
              {MEMBER_STATUS_LABELS[pending.status] ?? pending.status}」に
              変更します。よろしいですか？
            </span>
            <button className="btn-primary text-xs" onClick={confirmChange}>
              実行
            </button>
            <button className="btn-outline text-xs" onClick={cancelChange}>
              キャンセル
            </button>
          </div>
        </Banner>
      )}
      {loading ? (
        <Loading />
      ) : (
        <ScrollSyncTable>
          <table className="w-full min-w-[1040px] text-sm border border-black/10">
            <thead className="bg-black/5 text-left whitespace-nowrap">
              <tr>
                <th className="p-2">会員番号</th>
                <th className="p-2">表示名</th>
                <th className="p-2">会社名</th>
                <th className="p-2">メール</th>
                <th className="p-2">ステータス</th>
                <th className="p-2">事業ステージ</th>
                <th className="p-2">年商帯</th>
                <th className="p-2">従業員数</th>
                <th className="p-2">LINE</th>
                <th className="p-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-t border-black/10">
                  <td className="p-2 font-mono text-xs">
                    <Link
                      to={`/admin/members/${m.id}`}
                      className="text-tora underline"
                    >
                      {m.memberNo}
                    </Link>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      <span>{m.displayName}</span>
                      {m.isStaff ? (
                        <StatusBadge tone="neutral">スタッフ</StatusBadge>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-2 text-xs">{m.companyName || "-"}</td>
                  <td className="p-2">{m.email}</td>
                  <td className="p-2">
                    <StatusBadge
                      tone={MEMBER_STATUS_TONE[m.status] ?? "neutral"}
                    >
                      {MEMBER_STATUS_LABELS[m.status] ?? m.status}
                    </StatusBadge>
                  </td>
                  <td className="p-2 text-xs">{optLabel(STAGE_Q, m.businessStage ?? null)}</td>
                  <td className="p-2 text-xs">{optLabel(REVENUE_Q, m.revenueBand ?? null)}</td>
                  <td className="p-2 text-xs">{optLabel(EMPLOYEE_Q, m.employeeBand ?? null)}</td>
                  <td className="p-2">
                    {m.lineLinked ? (
                      <StatusBadge tone="success" bold>
                        ✓ 連携済
                      </StatusBadge>
                    ) : (
                      <span className="text-xs text-tora-ink/40">—</span>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/members/${m.id}`}
                        className="text-tora underline text-xs shrink-0"
                      >
                        詳細
                      </Link>
                      <select
                        className="input w-28 shrink-0"
                        value=""
                        onChange={(e) => {
                          if (e.target.value)
                            requestChange(m.id, e.target.value);
                        }}
                      >
                        <option value="">変更…</option>
                        <option value="active">有効</option>
                        <option value="payment_failed">決済失敗</option>
                        <option value="suspended">停止</option>
                        <option value="cancelled">解約</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollSyncTable>
      )}

      {!loading && total != null && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm">
          <span className="text-tora-ink/60">
            {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} /
            全 {total} 名
          </span>
          <div className="flex items-center gap-2">
            <button
              className="btn-outline text-xs px-3 py-1"
              disabled={page <= 1 || loading}
              onClick={() => load(page - 1)}
            >
              ← 前へ
            </button>
            <span className="text-tora-ink/60">
              {page} / {Math.max(1, Math.ceil(total / PER_PAGE))}
            </span>
            <button
              className="btn-outline text-xs px-3 py-1"
              disabled={page >= Math.ceil(total / PER_PAGE) || loading}
              onClick={() => load(page + 1)}
            >
              次へ →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
